import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ImageUp } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { useAdminApi } from "@/hooks/useAdminApi";
import { errMsg, slug, GAME_PLATFORMS } from "@/lib/api";
import { GameCover } from "@/components/store/GameCover";

const field = "w-full h-10 px-3 rounded-md bg-[#08090C] border border-white/10 text-sm focus:outline-none focus:border-emerald-500/60";
const emptyFor = (kind) => kind === "box"
  ? { name: "Mystery Box · 10 Codes", platform: "Mixed", kind, codes: 10, price: 20, stock: 50, description: "", image_url: "", active: true }
  : { name: "", platform: "Steam", kind, codes: 1, price: 9.99, stock: 10, description: "", image_url: "", active: true };

const GameForm = ({ game, kind, onClose, onSaved }) => {
  const call = useAdminApi();
  const [f, setF] = useState(game ? { ...emptyFor(kind), ...game, image_url: game.image_url || "" } : emptyFor(kind));
  const [tiers, setTiers] = useState(() => [0, 1, 2].map((i) => ({ min: game?.bulk_tiers?.[i]?.min ?? "", price: game?.bulk_tiers?.[i]?.price ?? "" })));
  const setTier = (i, k) => (e) => setTiers(tiers.map((t, j) => (j === i ? { ...t, [k]: e.target.value } : t)));
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (tiers.some((t) => (t.min === "") !== (t.price === ""))) { toast.error("Fill both quantity and price for each tier, or leave the row empty"); return; }
    const body = { name: f.name, platform: f.platform, kind, codes: Number(f.codes), price: Number(f.price), stock: Number(f.stock), description: f.description, image_url: f.image_url || null, active: f.active,
      ...(kind === "box" ? {} : { bulk_tiers: tiers.filter((t) => t.min !== "" && t.price !== "").map((t) => ({ min: Number(t.min), price: Number(t.price) })) }) };
    try {
      await call(game ? "put" : "post", game ? `/admin/games/${game.id}` : "/admin/games", body);
      toast.success(game ? "Saved" : "Added");
      onSaved();
    } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="game-form-dialog" className="bg-[#0F1117] border-white/10 text-white w-[calc(100vw-1.5rem)] max-h-[90vh] overflow-y-auto p-4 sm:p-6">
        <DialogHeader><DialogTitle className="font-display">{game ? "Edit" : "Add"} {kind === "box" ? "mystery box" : "game"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3 text-sm">
          <input data-testid="game-form-name" required placeholder="Name" value={f.name} onChange={set("name")} className={field} />
          <div className="grid grid-cols-3 gap-3">
            {kind === "box"
              ? <input data-testid="game-form-codes" type="number" min="1" required value={f.codes} onChange={set("codes")} className={`${field} font-mono`} />
              : <select data-testid="game-form-platform" value={f.platform} onChange={set("platform")} className={field}>{GAME_PLATFORMS.map((p) => <option key={p}>{p}</option>)}</select>}
            <input data-testid="game-form-price" type="number" step="0.01" min="0" required value={f.price} onChange={set("price")} className={`${field} font-mono`} />
            <input data-testid="game-form-stock" type="number" min={f.oversell ? undefined : 0} required value={f.stock} onChange={set("stock")} className={`${field} font-mono`} />
          </div>
          <p className="text-xs text-gray-500 -mt-1">{kind === "box" ? "Codes in box" : "Platform"} · Price (USD) · {kind === "box" ? "Boxes" : "Keys"} in stock</p>
          <input data-testid="game-form-image-url" placeholder="Image URL (optional — or upload from the list)" value={f.image_url} onChange={set("image_url")} className={field} />
          {kind !== "box" && (
            <>
              <p className="text-xs text-gray-400 pt-1">Reseller tiers (optional): min. quantity · price per key</p>
              {tiers.map((t, i) => (
                <div key={i} className="grid grid-cols-2 gap-3">
                  <input data-testid={`game-form-tier-min-${i}`} type="number" min="2" placeholder={`Tier ${i + 1} min`} value={t.min} onChange={setTier(i, "min")} className={`${field} font-mono`} />
                  <input data-testid={`game-form-tier-price-${i}`} type="number" step="0.01" min="0" placeholder="$ per key" value={t.price} onChange={setTier(i, "price")} className={`${field} font-mono`} />
                </div>
              ))}
              <p className="text-xs text-gray-500 -mt-1">Each tier must be cheaper than the one before. Leave all empty to turn bulk pricing off. Bulk games can be ordered beyond stock.</p>
            </>
          )}
          <textarea data-testid="game-form-description" rows={3} placeholder="Description" value={f.description} onChange={set("description")} className={`${field} h-auto py-2`} />
          <label className="flex items-center gap-2"><Switch data-testid="game-form-active" checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} /> Visible in store</label>
          <button data-testid="game-form-submit" className="w-full h-10 rounded-md bg-emerald-500 text-[#08090C] font-bold hover:bg-emerald-400">Save</button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const InlineNumber = ({ value, step, onSave, testid }) => {
  const [v, setV] = useState(value);
  useEffect(() => setV(value), [value]);
  const commit = () => { if (v !== "" && Number(v) !== value && Number(v) >= 0) onSave(Number(v)); else setV(value); };
  return (
    <input data-testid={testid} type="number" step={step} min="0" value={v} onChange={(e) => setV(e.target.value)} onBlur={commit}
      onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
      className="w-24 h-9 px-2 rounded-md bg-[#08090C] border border-white/10 font-mono text-sm focus:outline-none focus:border-emerald-500/60" />
  );
};

const Row = ({ g, onEdit, onReload }) => {
  const call = useAdminApi();
  const id = slug(g.name);
  const patch = async (body, msg) => {
    try { await call("patch", `/admin/games/${g.id}`, body); toast.success(msg); onReload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const upload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { await call("post", `/admin/games/${g.id}/image`, fd); toast.success("Image uploaded"); onReload(); } catch (err) { toast.error(errMsg(err)); }
  };
  const del = async () => {
    if (!window.confirm(`Remove ${g.name} from the store permanently?`)) return;
    await call("delete", `/admin/games/${g.id}`); toast.success("Removed"); onReload();
  };
  const inStock = g.stock > 0;
  return (
    <tr data-testid={`admin-game-row-${id}`} className="border-t border-white/5">
      <td className="p-3 hidden sm:table-cell"><div className="w-24 aspect-[460/215] rounded overflow-hidden"><GameCover game={g} compact /></div></td>
      <td className="p-3 min-w-[140px]">{g.name}<p className="text-xs text-gray-500">{g.kind === "box" ? `${g.codes} codes` : g.platform}</p></td>
      <td className="p-3"><InlineNumber testid={`admin-game-price-${id}`} value={g.price} step="0.01" onSave={(price) => patch({ price }, "Price updated")} /></td>
      <td className="p-3"><InlineNumber testid={`admin-game-stock-${id}`} value={g.stock} step="1" onSave={(stock) => patch({ stock }, "Stock updated")} /></td>
      <td className="p-3">
        <button data-testid={`admin-game-stock-toggle-${id}`} onClick={() => patch({ stock: inStock ? 0 : (g.kind === "box" ? 50 : 10) }, inStock ? "Marked out of stock" : "Back in stock")}
          className={`px-2 py-1 rounded text-xs font-semibold ${inStock ? "bg-emerald-500/15 text-emerald-400 hover:bg-red-500/15 hover:text-red-400" : "bg-red-500/15 text-red-400 hover:bg-emerald-500/15 hover:text-emerald-400"}`}>
          {inStock ? "In stock" : "Out of stock"}
        </button>
      </td>
      <td className="p-3">{g.kind !== "box" && <Switch data-testid={`admin-game-offer-${id}`} checked={!!g.offer} onCheckedChange={(v) => patch({ offer: v }, v ? "Added to Offer of the week" : "Removed from offers")} />}</td>
      <td className="p-3"><Switch data-testid={`admin-game-visible-${id}`} checked={g.active} onCheckedChange={(v) => patch({ active: v }, v ? "Shown in store" : "Hidden from store")} /></td>
      <td className="p-3">
        <div className="flex gap-1 justify-end">
          <label title="Upload image" className="p-2 rounded hover:bg-white/10 cursor-pointer"><ImageUp size={15} /><input data-testid={`admin-game-image-${id}`} type="file" accept="image/*" hidden onChange={upload} /></label>
          <button data-testid={`admin-game-edit-${id}`} onClick={() => onEdit(g)} className="p-3 sm:p-2 rounded hover:bg-white/10"><Pencil size={15} /></button>
          <button data-testid={`admin-game-delete-${id}`} onClick={del} className="p-3 sm:p-2 rounded hover:bg-red-500/10 text-red-400"><Trash2 size={15} /></button>
        </div>
      </td>
    </tr>
  );
};

export const GamesTab = ({ onChange, kind = "game" }) => {
  const call = useAdminApi();
  const [games, setGames] = useState([]);
  const [editing, setEditing] = useState(undefined);
  const [q, setQ] = useState("");
  const [vis, setVis] = useState("all");
  const [limit, setLimit] = useState(100);
  const load = () => { call("get", "/admin/games").then((all) => setGames(all.filter((g) => (g.kind || "game") === kind))); onChange(); };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const list = games.filter((g) => g.name.toLowerCase().includes(q.toLowerCase()) && (vis === "all" || (vis === "offer" ? g.offer : (vis === "visible") === g.active)));
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3 justify-between">
        <div className="flex flex-wrap lg:flex-nowrap items-center gap-2 w-full sm:w-auto">
          <input data-testid={`admin-${kind}-search`} value={q} onChange={(e) => { setQ(e.target.value); setLimit(100); }} placeholder="Filter..." className={`${field.replace("w-full ", "")} w-full sm:w-64 shrink-0`} />
          {["all", "visible", "hidden", "offer"].map((v) => (
            <button key={v} data-testid={`admin-${kind}-filter-${v}`} onClick={() => { setVis(v); setLimit(100); }}
              className={`h-10 px-3 rounded-md text-sm capitalize ${vis === v ? "bg-white/10 text-white" : "text-gray-400 hover:text-white"}`}>{v}</button>
          ))}
        </div>
        <button data-testid={`admin-add-${kind}-button`} onClick={() => setEditing(null)} className="h-10 px-4 rounded-md bg-emerald-500 text-[#08090C] font-bold inline-flex items-center gap-2 hover:bg-emerald-400"><Plus size={16} /> Add {kind === "box" ? "box" : "game"}</button>
      </div>
      <p className="text-xs text-gray-500">Edit price or stock directly in the table — changes save when you leave the field or press Enter. Imported titles start hidden at $0: set a price, then switch “Visible” on. <span className="font-mono">{list.length}</span> shown.</p>
      <div className="rounded-lg border border-white/10 bg-[#0F1117] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-gray-500 label-eyebrow"><tr>{["", "Name", "Price $", "Stock", "Status", "Offer", "Visible", ""].map((h, i) => <th key={i} className={`p-3 font-semibold ${i === 0 ? "hidden sm:table-cell" : ""}`}>{h}</th>)}</tr></thead>
          <tbody>
            {list.slice(0, limit).map((g) => <Row key={g.id} g={g} onEdit={setEditing} onReload={load} />)}
          </tbody>
        </table>
      </div>
      {list.length > limit && (
        <button data-testid={`admin-${kind}-show-more`} onClick={() => setLimit(limit + 100)} className="w-full h-10 rounded-md border border-white/10 text-sm hover:border-emerald-500/60">
          Show more ({list.length - limit} left)
        </button>
      )}
      {editing !== undefined && <GameForm game={editing} kind={kind} onClose={() => setEditing(undefined)} onSaved={() => { setEditing(undefined); load(); }} />}
    </div>
  );
};
